<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue';
import type { RecommendItem } from '@/utils/type';
import { CONTENT_TYPE } from '@/utils/const';
import ExpandBtn from './components/ExpandBtn/ExpandBtn.vue';
import CollapseBtn from './components/CollapseBtn/CollapseBtn.vue';
import FullContent from './components/FullContent/FullContent.vue';

const props = withDefaults(
    defineProps<{
        // 内容数据
        item: RecommendItem;
        // 是否展开
        expanded: boolean;
        // 摘要行数
        abstractRow: number;
        // 是否隐藏关闭按钮
        hideCloseBtn: boolean;
    }>(),
    {},
);

const emits = defineEmits<{
    expandContent: [];
    closeContent: [];
}>();

// 获取摘要内容
const abstract = computed(() => {
    // 将所有 item.type === CONTENT_TYPE.TEXT 的 item.content 拼接起来
    return props.item.contentList
        .filter((item) => item.type === CONTENT_TYPE.TEXT)
        .map((item) => item.content)
        .join('');
});

const articleRef = ref<HTMLDivElement | null>(null);

const handleClick = async (shouldExpand: boolean) => {
    const article = articleRef.value;
    if (!article) return;

    // 暂停过渡效果, 准备获取高度
    article.style.transition = 'none';

    // 获取当前高度
    const currentHeight = article.offsetHeight;

    // 切换状态
    shouldExpand ? emits('expandContent') : emits('closeContent');

    // 等待 DOM 更新
    await nextTick();

    // 获取目标高度
    const targetHeight = article.offsetHeight;

    // 执行高度动画 (展示 400ms, 收起 200ms)
    await animateHeight(
        article,
        currentHeight,
        targetHeight,
        shouldExpand ? 400 : 200,
    );
};

const animateHeight = async (
    element: HTMLElement,
    fromHeight: number,
    toHeight: number,
    duration: number,
) => {
    // 设置起始高度
    element.style.height = fromHeight + 'px';

    // 强制重绘
    void element.offsetHeight;

    // 添加过渡效果并设置目标高度
    element.style.transition = `height ${duration / 1000}s`;
    element.style.height = toHeight + 'px';

    // 动画结束后清理样式
    return new Promise<void>((resolve) => {
        setTimeout(() => {
            element.style.height = '';
            element.style.transition = '';
            resolve();
        }, duration);
    });
};
</script>

<template>
    <div class="article" ref="articleRef">
        <template v-if="!expanded">
            <!-- 摘要 -->
            <p class="abstract" :style="{ '--abstract-row': abstractRow }">
                {{ abstract }}
            </p>
            <!-- 展开按钮 -->
            <ExpandBtn @click="handleClick(true)" />
        </template>

        <template v-else>
            <!-- 全文 -->
            <FullContent :contentList="item.contentList" />
            <!-- 收起按钮 -->
            <CollapseBtn @click="handleClick(false)" v-if="!hideCloseBtn" />
        </template>
    </div>
</template>

<style lang="scss" scoped>
@use './MainContent.scss';
</style>
